import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { WeeklyCustomOptionSelector } from "./WeeklyCustomOptionSelector";
import { TimeZone } from "client/DateAndTime/timezones";
import { MonthlyCustomOptionSelector } from "./MonthlyCustomOptionSelector";
import { YearlyCustomOptionSelector } from "./YearlyCustomOptionSelector";
import {
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CustomRecurrenceOptions.module.less";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { isValidDate } from "client/DateAndTime/dateTimeUtils";
import { CustomRecurrenceOptionsModel, CustomRecurrencePatternKind } from "../../common";
import { never } from "client_legacy/ndaw/errors";

export type CustomRecurrenceOptionsProps = {
	attachable: IValueInputAttachable<CustomRecurrenceOptionsModel>;
	timezone: TimeZone;
	startDate: Date;
};

export const CustomRecurrenceOptions = (props: CustomRecurrenceOptionsProps): ReactElement => {
	const {
		attachable,
		startDate,
		timezone
	} = props;

	const [validStartDate, setValidStartDate] = useState<Date>(
		() => isValidDate(startDate) ? startDate : new Date()
	);

	useEffect(
		() => {
			if (isValidDate(startDate)) {
				setValidStartDate(startDate);
			}
		},
		[startDate]
	);

	return (
		<>
			<FormItem
				label={localize("_RepeatEvery")}
			>
				<div className={styles.repeatEveryRow}>
					<div>
						<Select
							{...attachable.for("repeatInterval").attachToSelect()}
							menuTitle={localize("_RepeatEvery")}
							options={repeatEveryOptions}
						/>
						<ValidationError {...attachable.for("repeatInterval").attachToValidationError()} />
					</div>
					<div>
						<CustomRecurrenceKindSelect
							attachable={attachable.for("kind")}
						/>
					</div>
				</div>
			</FormItem>
			{attachable.when(
				x => x.kind !== CustomRecurrencePatternKind.Daily,
				handler => (
					<RepeatOn
						startDate={validStartDate}
						timezone={timezone}
						attachable={handler}
					/>
				)
			)}

		</>
	);
};
CustomRecurrenceOptions.displayName="CustomRecurrenceOptions";

type CustomRecurrenceKindSelectProps = {
	attachable: IValueInputAttachable<CustomRecurrencePatternKind>;
};

const CustomRecurrenceKindSelect = (props: CustomRecurrenceKindSelectProps): ReactElement => {
	const {
		attachable
	} = props;

	return (
		<>
			<Select
				{...attachable.attachToSelect()}
				menuTitle={localize("_Repeat")}
				options={recurenceKindOptions}
			/>
			<ValidationError {...attachable.attachToValidationError()} />
		</>
	);
};
CustomRecurrenceKindSelect.displayName = "CustomRecurrenceKindSelect";

const recurenceKindOptions: readonly SelectItem<CustomRecurrencePatternKind>[]= [
	{
		text: localize("_Day"),
		value: CustomRecurrencePatternKind.Daily
	},
	{
		text: localize("_Week"),
		value: CustomRecurrencePatternKind.Weekly
	},
	{
		text: localize("_Month"),
		value: CustomRecurrencePatternKind.Monthly
	},
	{
		text: localize("_Year"),
		value: CustomRecurrencePatternKind.Yearly
	}
];

type RepeatOnProps = {
	attachable: IValueInputAttachable<CustomRecurrenceOptionsModel>;
	timezone: TimeZone;
	startDate: Date;
};

const RepeatOn = (props: RepeatOnProps): ReactElement | null => {
	const {
		attachable,
		timezone,
		startDate
	} = props;

	const { kind } = attachable
		.reducer
		.value;

	switch(kind) {
		case CustomRecurrencePatternKind.Weekly:
			return (
				<WeeklyCustomOptionSelector
					handler={attachable.for("weeklyCustomRecurrenceOptions")}
				/>
			);
		case CustomRecurrencePatternKind.Monthly:
			return (
				<MonthlyCustomOptionSelector
					handler={attachable.for("monthlyCustomRecurrenceOptions")}
					timezone={timezone}
					startDate={startDate}
				/>
			);
		case CustomRecurrencePatternKind.Yearly:
			return (
				<YearlyCustomOptionSelector
					handler={attachable.for("yearlyCustomRecurrenceOptions")}
					timezone={timezone}
					startDate={startDate}
				/>
			);
		case CustomRecurrencePatternKind.Daily:
			return null;
		default:
			never(kind);
	}
};
RepeatOn.displayName = "RepeatOn";

const repeatEveryOptions: readonly SelectItem<number>[] = range(1, 100).map(
	(x: number): SelectItem<number> => ({
		text: x.toString(),
		value: x
	})
);